import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Avatar,
  Chip
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";

import api from "../../services/api";
import usePlans from "../../hooks/usePlans";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Global Styles (mesmo do Dashboard)
// ---------------------------------------------------------------------------
const globalStyles = `
  @keyframes pulse-ring {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}

// ---------------------------------------------------------------------------
// Initial Settings
// ---------------------------------------------------------------------------
const initialSettings = {
  messageInterval: 20,
  longerIntervalAfter: 20,
  greaterInterval: 60,
  variables: [],
  sabado: "false",
  domingo: "false",
  startHour: "09:00",
  endHour: "18:00"
};

// ---------------------------------------------------------------------------
// CampaignsConfig
// ---------------------------------------------------------------------------
const CampaignsConfig = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  // --- State ---
  const [settings, setSettings] = useState(initialSettings);
  const [showVariablesForm, setShowVariablesForm] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [variable, setVariable] = useState({ key: "", value: "" });
  const [sabado, setSabado] = useState(false);
  const [domingo, setDomingo] = useState(false);
  const [startHour, setStartHour] = useState("08:00");
  const [endHour, setEndHour] = useState("19:00");

  // ---------------------------------------------------------------------------
  // Permissão do plano
  // ---------------------------------------------------------------------------
  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, user.companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch settings
  // ---------------------------------------------------------------------------
  useEffect(() => {
    api.get("/campaign-settings").then(({ data }) => {
      const settingsList = [];
      console.log(data);
      if (Array.isArray(data) && data.length > 0) {
        data.forEach((item) => {
          settingsList.push([item.key, item.value]);
          if (item.key === "sabado") setSabado(item?.value === "true");
          if (item.key === "domingo") setDomingo(item?.value === "true");
          if (item.key === "startHour") setStartHour(item?.value);
          if (item.key === "endHour") setEndHour(item?.value);
        });
        setSettings(Object.fromEntries(settingsList));
      }
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleOnChangeVariable = (e) => {
    if (e.target.value !== null) {
      const changedProp = {};
      changedProp[e.target.name] = e.target.value;
      setVariable((prev) => ({ ...prev, ...changedProp }));
    }
  };

  const handleOnChangeSettings = (e) => {
    const changedProp = {};
    changedProp[e.target.name] = e.target.value;
    setSettings((prev) => ({ ...prev, ...changedProp }));
  };

  const addVariable = () => {
    setSettings((prev) => {
      if (!Array.isArray(prev.variables)) {
        return { ...prev, variables: [Object.assign({}, variable)] };
      }
      const variablesExists = settings.variables.filter((v) => v.key === variable.key);
      const variables = prev.variables;
      if (variablesExists.length === 0) {
        variables.push(Object.assign({}, variable));
        setVariable({ key: "", value: "" });
      }
      return { ...prev, variables };
    });
  };

  const removeVariable = () => {
    const newList = settings.variables.filter((v) => v.key !== selectedKey);
    setSettings((prev) => ({ ...prev, variables: newList }));
    setSelectedKey(null);
  };

  const saveSettings = async () => {
    await api.post("/campaign-settings", { settings });
    toast.success("Configurações salvas");
  };

  const handleChange = (event) => {
    if (event.target.name === "sabado") {
      setSabado(event.target.checked);
    }
    if (event.target.name === "domingo") {
      setDomingo(event.target.checked);
    }
  };

  const handleSaveTimeMass = async () => {
    let settingsTime = {
      sabado: sabado,
      domingo: domingo,
      startHour: startHour,
      endHour: endHour
    };

    try {
      await api.post(`/campaign-settings/`, { settings: settingsTime });
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <SettingsIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("campaignsConfig.title")}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Configure intervalos e comportamento das campanhas
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Conteúdo */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          {/* Cabeçalho do Paper */}
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TuneIcon sx={{ color: PRIMARY_MAIN, fontSize: 24 }} />
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                Configurações de Intervalos
              </Typography>
            </Stack>
          </Box>

          {/* Grid de configurações */}
          <Box sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {/* Intervalo Randômico */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="messageInterval-label">
                    {i18n.t("campaigns.settings.randomInterval")}
                  </InputLabel>
                  <Select
                    name="messageInterval"
                    id="messageInterval"
                    labelId="messageInterval-label"
                    label={i18n.t("campaigns.settings.randomInterval")}
                    value={settings.messageInterval}
                    onChange={(e) => handleOnChangeSettings(e)}
                    sx={{
                      borderRadius: 2,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "divider"
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: PRIMARY_MAIN
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: PRIMARY_MAIN
                      }
                    }}
                  >
                    <MenuItem value={0}>{i18n.t("campaigns.settings.noBreak")}</MenuItem>
                    <MenuItem value={5}>5 segundos</MenuItem>
                    <MenuItem value={10}>10 segundos</MenuItem>
                    <MenuItem value={15}>15 segundos</MenuItem>
                    <MenuItem value={20}>20 segundos</MenuItem>
                    <MenuItem value={30}>30 segundos</MenuItem>
                    <MenuItem value={60}>40 segundos</MenuItem>
                    <MenuItem value={70}>60 segundos</MenuItem>
                    <MenuItem value={80}>80 segundos</MenuItem>
                    <MenuItem value={100}>100 segundos</MenuItem>
                    <MenuItem value={120}>120 segundos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Intervalo maior após */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="longerIntervalAfter-label">
                    {i18n.t("campaigns.settings.intervalGapAfter")}
                  </InputLabel>
                  <Select
                    name="longerIntervalAfter"
                    id="longerIntervalAfter"
                    labelId="longerIntervalAfter-label"
                    label={i18n.t("campaigns.settings.intervalGapAfter")}
                    value={settings.longerIntervalAfter}
                    onChange={(e) => handleOnChangeSettings(e)}
                    sx={{
                      borderRadius: 2,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "divider"
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: PRIMARY_MAIN
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: PRIMARY_MAIN
                      }
                    }}
                  >
                    <MenuItem value={0}>{i18n.t("campaigns.settings.undefined")}</MenuItem>
                    <MenuItem value={5}>5 {i18n.t("campaigns.settings.messages")}</MenuItem>
                    <MenuItem value={10}>10 {i18n.t("campaigns.settings.messages")}</MenuItem>
                    <MenuItem value={15}>15 {i18n.t("campaigns.settings.messages")}</MenuItem>
                    <MenuItem value={20}>20 {i18n.t("campaigns.settings.messages")}</MenuItem>
                    <MenuItem value={30}>30 {i18n.t("campaigns.settings.messages")}</MenuItem>
                    <MenuItem value={40}>40 {i18n.t("campaigns.settings.messages")}</MenuItem>
                    <MenuItem value={50}>50 {i18n.t("campaigns.settings.messages")}</MenuItem>
                    <MenuItem value={60}>60 {i18n.t("campaigns.settings.messages")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Intervalo Maior de Disparo */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="greaterInterval-label">
                    {i18n.t("campaigns.settings.laggerTriggerRange")}
                  </InputLabel>
                  <Select
                    name="greaterInterval"
                    id="greaterInterval"
                    labelId="greaterInterval-label"
                    label={i18n.t("campaigns.settings.laggerTriggerRange")}
                    value={settings.greaterInterval}
                    onChange={(e) => handleOnChangeSettings(e)}
                    sx={{
                      borderRadius: 2,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "divider"
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: PRIMARY_MAIN
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: PRIMARY_MAIN
                      }
                    }}
                  >
                    <MenuItem value={0}>{i18n.t("campaigns.settings.noBreak")}</MenuItem>
                    <MenuItem value={20}>20 segundos</MenuItem>
                    <MenuItem value={30}>30 segundos</MenuItem>
                    <MenuItem value={40}>40 segundos</MenuItem>
                    <MenuItem value={50}>50 segundos</MenuItem>
                    <MenuItem value={60}>60 segundos</MenuItem>
                    <MenuItem value={70}>70 segundos</MenuItem>
                    <MenuItem value={80}>80 segundos</MenuItem>
                    <MenuItem value={90}>90 segundos</MenuItem>
                    <MenuItem value={100}>100 segundos</MenuItem>
                    <MenuItem value={110}>110 segundos</MenuItem>
                    <MenuItem value={120}>120 segundos</MenuItem>
                    <MenuItem value={130}>130 segundos</MenuItem>
                    <MenuItem value={140}>140 segundos</MenuItem>
                    <MenuItem value={150}>150 segundos</MenuItem>
                    <MenuItem value={160}>160 segundos</MenuItem>
                    <MenuItem value={170}>170 segundos</MenuItem>
                    <MenuItem value={180}>180 segundos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Botão de salvar */}
              <Grid item xs={12}>
                <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 3, mt: 2 }}>
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={saveSettings}
                      sx={{
                        bgcolor: PRIMARY_MAIN,
                        color: "#fff",
                        borderRadius: 2,
                        px: 3,
                        py: 1.25,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                        "&:hover": {
                          bgcolor: PRIMARY_DARK,
                          boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                        }
                      }}
                    >
                      {i18n.t("campaigns.settings.save")}
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      {/* Modais */}
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={removeVariable}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
    </Box>
  );
};

export default CampaignsConfig;